import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Brain, Keyboard, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EDUCATION_LEVELS = [
  "SD",
  "SMP",
  "SMA/SMK",
  "D3",
  "S1",
  "S2",
  "S3"
];

export default function RegistrationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nama: "",
    email: "",
    no_hp: "",
    usia: "",
    tingkat_pendidikan: "",
    jurusan: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, tingkat_pendidikan: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nama || !formData.email || !formData.no_hp || !formData.usia || !formData.tingkat_pendidikan || !formData.jurusan) {
      toast.error("Mohon lengkapi semua data");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/participants/register`, {
        ...formData,
        usia: parseInt(formData.usia)
      });
      
      toast.success("Pendaftaran berhasil!");
      navigate(`/iq-test/${response.data.participant_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Terjadi kesalahan saat mendaftar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 md:p-8"
      style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.85), rgba(255,255,255,0.85)), url('https://images.pexels.com/photos/950241/pexels-photo-950241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="w-full max-w-lg animate-fadeIn">
        {/* Header */}
        <div className="text-left mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-[#002FA7] flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-[#0A0A0A]">
              SBW IQ Tester
            </h1>
          </div>
          <p className="text-[#475569] text-base leading-relaxed">
            Platform penilaian IQ dan kecepatan mengetik. Lengkapi data diri Anda untuk memulai test.
          </p>
        </div>

        {/* Features */}
        <div className="flex gap-4 mb-8">
          <div className="flex items-center gap-2 text-sm text-[#475569]">
            <Brain className="w-4 h-4 text-[#002FA7]" />
            <span>30 Soal IQ</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#475569]">
            <Keyboard className="w-4 h-4 text-[#002FA7]" />
            <span>Test Mengetik 5 Menit</span>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white border border-slate-200 p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="nama" className="text-sm font-medium text-[#0A0A0A]">
                Nama Lengkap
              </Label>
              <Input
                id="nama"
                name="nama"
                type="text"
                placeholder="Masukkan nama lengkap"
                value={formData.nama}
                onChange={handleChange}
                className="h-11 border-slate-200 rounded-sm focus:border-[#002FA7]"
                data-testid="input-nama"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-[#0A0A0A]">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="contoh@email.com"
                value={formData.email}
                onChange={handleChange}
                className="h-11 border-slate-200 rounded-sm focus:border-[#002FA7]"
                data-testid="input-email"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="no_hp" className="text-sm font-medium text-[#0A0A0A]">
                  No. HP
                </Label>
                <Input
                  id="no_hp"
                  name="no_hp"
                  type="tel"
                  placeholder="08xxxxxxxxxx"
                  value={formData.no_hp}
                  onChange={handleChange}
                  className="h-11 border-slate-200 rounded-sm focus:border-[#002FA7]"
                  data-testid="input-no-hp"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="usia" className="text-sm font-medium text-[#0A0A0A]">
                  Usia
                </Label>
                <Input
                  id="usia"
                  name="usia"
                  type="number"
                  placeholder="Tahun"
                  min="10"
                  max="100"
                  value={formData.usia}
                  onChange={handleChange}
                  className="h-11 border-slate-200 rounded-sm focus:border-[#002FA7]"
                  data-testid="input-usia"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tingkat_pendidikan" className="text-sm font-medium text-[#0A0A0A]">
                Tingkat Pendidikan
              </Label>
              <Select onValueChange={handleSelectChange} value={formData.tingkat_pendidikan}>
                <SelectTrigger 
                  className="h-11 border-slate-200 rounded-sm"
                  data-testid="select-pendidikan"
                >
                  <SelectValue placeholder="Pilih tingkat pendidikan" />
                </SelectTrigger>
                <SelectContent>
                  {EDUCATION_LEVELS.map((level) => (
                    <SelectItem key={level} value={level}>
                      {level}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="jurusan" className="text-sm font-medium text-[#0A0A0A]">
                Jurusan / Bidang Studi
              </Label>
              <Input
                id="jurusan"
                name="jurusan"
                type="text"
                placeholder="Contoh: Teknik Informatika"
                value={formData.jurusan}
                onChange={handleChange}
                className="h-11 border-slate-200 rounded-sm focus:border-[#002FA7]"
                data-testid="input-jurusan"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#002FA7] hover:bg-[#00237C] text-white font-semibold rounded-sm transition-colors"
              data-testid="btn-mulai-test"
            >
              {loading ? (
                "Memproses..."
              ) : (
                <>
                  Mulai Test
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Admin Link */}
        <div className="mt-6 text-center">
          <a 
            href="/admin" 
            className="text-sm text-[#475569] hover:text-[#002FA7] transition-colors"
            data-testid="link-admin"
          >
            Admin Login
          </a>
        </div>
      </div>
    </div>
  );
}
